import { mdiPlusCircle } from '@mdi/js';
import Icon from '@mdi/react';
import {
  Button,
  Checkbox,
  Col,
  Input, Modal,
  Row,
} from 'antd';
import _, { get } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import TagsSettingList from '../../components/TagsSettingList/TagsSettingList';
import ListOfGroupsAsTagsInModal from './ListOfGroupsAsTagsInModal';

import { capitalize } from '../../mainUtils';
import { getDefaultGroupNames } from '../selectors';
import { ApiContext } from '../../api/ApiContextProvider';
import BaseIcon from '../../_ui/BaseIcon/BaseIcon';
import BaseButton from '../../_ui/BaseButton/BaseButton';

function ActorsPageHeader({
  actorUUID,
  isCreateView,
  onChangeSearch,
  onSearchClick,
  onSelectTagsCallback,
  switchView,
  searchValue,
  createdActor,
  groupUuidForDeletion,
}) {
  const { t } = useTranslation();

  const { requestGetAllGroups } = useContext(ApiContext);

  const defaultGroupNames = useSelector(getDefaultGroupNames);

  const [allTagsVisible, setAllTagsVisible] = useState(false);
  const [limit, setLimit] = useState(10);
  const [totalTags, setTotalTags] = useState(0);
  const [needToReset, setNeedToReset] = useState(false);
  const prevLimitRef = useRef(limit);

  const showMoreBtn = {
    uuid: 'addMore',
    actor_type: 'group',
    uinfo: { group_name: 'show more...' },
    unselectable: true,
  };

  const [tagsOfGroups, changeTagsOfGroups] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [groupTags, changeGroupTags] = useState([]);
  const [defaultSelectedTags, setDefaultSelectedTags] = useState([{
    key: 'all',
    value: 'all',
  }]);

  const cancelModalFilters = () => {
    setIsModalVisible(false);
  };

  const onSelectTags = (filters) => {
    let changedFilters = [];

    const findFilter = (name) => filters.findIndex((item) => item.value === name) !== -1;

    if (findFilter('addMore')) {
      setLimit(totalTags);
      setAllTagsVisible(!allTagsVisible);
    }

    if (filters.filter((item) => item.value !== 'all').length === 1 && findFilter('user') && !findFilter('unbanned')) {
      changedFilters = [
        {
          key: 'unban_actors',
          status: 'excluded',
          value: 'unbanned',
        },
        ...filters,
      ].filter((item) => item.value !== 'all');

      setDefaultSelectedTags(changedFilters);
    } else {
      changedFilters = filters.filter((item) => item.value !== 'unbanned');
      setDefaultSelectedTags(changedFilters);
    }

    // console.log('changedFilters', changedFilters)

    onSelectTagsCallback?.(changedFilters);
  };

  const initFunc = async () => {
    const allGroups = await requestGetAllGroups({
      limit,
      offset: 0,
      order_by_column: 'created',
      order_by_rule: 'asc',
    });

    const {
      total = 0,
    } = allGroups;

    setTotalTags(total);

    // console.log('INIT groups', get(allGroups, 'actors', []))

    const actorGroupsUnlessBan = get(allGroups, 'actors', []).filter((item) => true);

    changeGroupTags(actorGroupsUnlessBan);
  };

  const filterTags = [
    {
      tags: [
        {
          key: 'all',
          value: 'all',
          label: capitalize(t('auth.headers.all_actors', 'all actors')),
        },
      ],
      isReset: true,
      isRadio: true,
    },
    {
      tags: [
        {
          key: 'unban_actors',
          value: 'unbanned',
          label: capitalize(t('auth.headers.unbanned', 'banned')),
        },
      ],
      isExcluded: true,
    },
    {
      tags: [
        {
          key: 'actor_type',
          value: 'user',
          label: capitalize(t('auth.headers.users', 'users')),
        },
        {
          key: 'actor_type',
          value: 'group',
          label: capitalize(t('auth.headers.groups', 'groups')),
        },
        {
          key: 'actor_type',
          value: 'service',
          label: capitalize(t('auth.headers.services', 'services')),
        },
        {
          key: 'is_root',
          value: 'true',
          label: 'Is ROOT',
          className: 'ml-6 ROOT',
        },
      ],
    },
    {
      tags: [
        ...tagsOfGroups.map((item) => {
          const label = _.get(item, 'uinfo.group_name');

          const propsOfTag = {
            className: item.uuid === 'addMore' ? 'show-more' : defaultGroupNames.includes(label) ? label : '',
          };

          if (item.value && item.label) {
            return item;
          }
          return {
            key: 'uinfo.groups',
            value: item.uuid,
            label,
            unselectable: get(item, 'unselectable', false),
            ...propsOfTag,
          };
        }),
      ],
      isRow: true,
      rowText: capitalize(t('auth.headers.groups', 'groups')),
      className: 'isGroups',
    },
  ];

  const resetFunc = () => {
    if (createdActor) {
      changeTagsOfGroups([]);
      changeGroupTags([]);
      setDefaultSelectedTags([{
        key: 'all',
        value: 'all',
      }]);
      setAllTagsVisible(false);
      setLimit(10);
      const prevLimit = prevLimitRef.current;
      if (prevLimit === limit) {
        initFunc();
      }
      prevLimitRef.current = limit;
      setNeedToReset(true);
    }
  };

  useEffect(() => {
    const newMapArr = new Map([...groupTags, ...tagsOfGroups, showMoreBtn].map((item) => [item.uuid || item.value, item]));

    if (allTagsVisible) {
      changeTagsOfGroups([...newMapArr].filter((item) => item[0] !== 'addMore').map(([name, value]) => value));
    } else {
      changeTagsOfGroups([...newMapArr].map(([name, value]) => value));
    }
  }, [groupTags, allTagsVisible]);

  useEffect(() => {
    resetFunc();
  }, [createdActor]);

  useEffect(() => {
    initFunc();
  }, [limit]);

  return (
    <>
      <h4 className="header-primary mb-0 mr-4">
        {capitalize(t('auth.pages.actors_filters', 'actors filters'))}
      </h4>
      <hr className="my-6" />
      <Row>
        <Col>
          <TagsSettingList
            defaultSelectedTags={defaultSelectedTags}
            allTags={filterTags}
            onSelectTags={onSelectTags}
            id="allActors"
            small
            createdActor={createdActor}
            groupUuidForDeletion={groupUuidForDeletion}
            needToReset={needToReset}
            setNeedToReset={setNeedToReset}
          />
        </Col>
      </Row>
      <Modal
        title="Add filters"
        open={isModalVisible}
        destroyOnClose
        footer={[
          <BaseButton key="back" onClick={cancelModalFilters}>
            Cancel
          </BaseButton>,
          <BaseButton
            key="submit"
            type="primary"
            disabled={groupTags.length === 0}
            onClick={cancelModalFilters}
          >
            Add filters
          </BaseButton>,
        ]}
        onCancel={cancelModalFilters}
      >
        {isModalVisible && (
        <ListOfGroupsAsTagsInModal
          getSelectedTags={(tags) => changeGroupTags([...groupTags, ...tags])}
          groupTagsOnActorsPage={groupTags}
        />
        )}
      </Modal>
    </>
  );
}

export default ActorsPageHeader;

ActorsPageHeader.propTypes = {
  actorUUID: PropTypes.string,
  isCreateView: PropTypes.bool,
  onChangeSearch: PropTypes.func.isRequired,
  onSelectTags: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array,
  switchView: PropTypes.func.isRequired,
  createdActor: PropTypes.object,
  groupUuidForDeletion: PropTypes.string,
};
